import React, { useRef } from "react";
import { Button } from "antd";
import "antd/dist/reset.css";
// import UploadCloudSvg from "../../../../../../assets/svgs/Upload cloud.svg";
import EyeSvg from "../../../../../../assets/svgs/eye.svg";
import { Upload, Eye } from "lucide-react";
import toast from "react-hot-toast";
import Input from "../../../../../../components/Elements/Input/Input";
import PhoneNumberInput from "../../../../../../components/Elements/Input/PhoneNumberInput";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProfileData,
  sendBillingOtp,
  verifyBillingOtp,
  resetBillingOtpState,
} from "../../../../../../redux/ProfileSlice/profileSlice";
import {
  getProfileImageUrl,
  isBillingEmailChangeRejected,
} from "../profileFormUtils";

const splitContactNumber = (contactNumber = "", fallbackCountryCode = "") => {
  if (!contactNumber) {
    return { countryCode: fallbackCountryCode, phoneNumber: "" };
  }
  if (fallbackCountryCode && contactNumber.startsWith(fallbackCountryCode)) {
    return {
      countryCode: fallbackCountryCode,
      phoneNumber: contactNumber.slice(fallbackCountryCode.length),
    };
  }
  const matchedCountryCode = contactNumber.match(/^\+\d{1,4}/)?.[0] || "";
  return {
    countryCode: matchedCountryCode || fallbackCountryCode,
    phoneNumber: matchedCountryCode
      ? contactNumber.slice(matchedCountryCode.length)
      : contactNumber,
  };
};

/**
 * ContactDetails is a fully controlled component.
 * All state (contactDetails, passportFile, otp) is owned by OrganizationDetails
 * and passed in as props. The Save button is removed; saving is handled by the
 * parent so that the billing payload is merged in a single API call.
 */
const ContactDetails = ({
  contactDetails,
  setContactDetails,
  passportFile,
  setPassportFile,
  otp,
  setOtp,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { profileData, billingOtpLoading, billingOtpSent, billingOtpVerified } =
    useSelector((state) => state.profile);
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;
  const passportInputRef = useRef(null);

  const handleChange = (e) => {
    // Only reset OTP state when there's actually something to reset (an
    // OTP was sent and/or verified for a previous email draft). Dispatching
    // resetBillingOtpState() unconditionally on every keystroke changes the
    // `profile` slice's reference on every keystroke, which — if anything
    // upstream selects the whole slice without shallowEqual and hydrates
    // form state from it — can wipe out what the user is actively typing.
    // Gating on billingOtpSent/billingOtpVerified keeps the reset meaningful
    // (email draft changed after an OTP flow was already in progress)
    // without firing on every character.
    if (e.target.name === "emailId" && (billingOtpSent || billingOtpVerified)) {
      dispatch(resetBillingOtpState());
      setOtp("");
    }
    setContactDetails((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSendCode = async () => {
    const email = contactDetails.emailId?.trim();
    if (!email) {
      toast.error("Please enter an email first.");
      return;
    }
    try {
      const result = await dispatch(sendBillingOtp({ email })).unwrap();
      toast.success(result?.message || `OTP sent successfully to ${email}`);
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Failed to send OTP.");
    }
  };

  const handleVerifyOtp = async () => {
    const email = contactDetails.emailId?.trim();
    if (!otp) {
      toast.error("Please enter the OTP.");
      return;
    }
    if (!email) {
      toast.error("Please enter an email first.");
      return;
    }
    try {
      const result = await dispatch(verifyBillingOtp({ email, otp })).unwrap();
      toast.success(result?.message || "OTP verified successfully!");
      setOtp("");
      if (user?._id) {
        dispatch(fetchProfileData(user._id));
      }
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Verification failed.");
    }
  };

  const passportUrl = getProfileImageUrl(contactDetails.copyOfPassport);

  const savedBillingEmail = profile?.billing?.billingContact?.email || "";
  const pendingBillingEmail =
    profile?.billing?.billingContact?.emailVerification?.pendingEmail || "";
  const isProfileEmailVerified =
    profile?.billing?.billingContact?.emailVerification?.isVerified === true;

  const trimmedDraftEmail = (contactDetails.emailId || "").trim();
  const hasDraftEmail = trimmedDraftEmail !== "";

  // The field is no longer prefilled, so a blank draft must NOT be treated
  // as "the user wants to change the email" — only a non-empty draft that
  // differs from what's saved counts as a real change. buildBillingPayload
  // already mirrors this: a blank draft falls back to the saved email on
  // submit, so this just keeps the on-screen warning consistent with that.
  const emailChanged =
    hasDraftEmail &&
    isBillingEmailChangeRejected(trimmedDraftEmail, savedBillingEmail);

  // A pending, not-yet-verified email change exists on the profile,
  // independent of whatever (blank) value is currently in the input.
  const hasPendingVerificationOnProfile =
    Boolean(pendingBillingEmail) && !isProfileEmailVerified;

  // If the user is actively typing a draft that matches the pending email,
  // treat that as "resuming" the pending verification.
  const hasPendingVerification =
    hasPendingVerificationOnProfile &&
    hasDraftEmail &&
    pendingBillingEmail.trim().toLowerCase() ===
      trimmedDraftEmail.toLowerCase();

  // Show "verified" only when nothing new has been typed — i.e. the saved,
  // verified email is what will actually be submitted.
  const emailVerifiedFromProfile = isProfileEmailVerified && !hasDraftEmail;

  const showOtpInput = billingOtpSent && !billingOtpVerified;
  const showEmailVerified =
    (emailVerifiedFromProfile || billingOtpVerified) && !showOtpInput;
  const passportViewUrl = passportFile
    ? URL.createObjectURL(passportFile)
    : passportUrl;
  return (
    <div className="w-full">
      {/* Contact Person Name */}
      <div className="mb-6">
        <label className="block font-normal text-gray-500 mb-2">
          Contact Person Name
        </label>
        <Input
          type="text"
          name="contactPersonName"
          value={contactDetails.contactPersonName}
          onChange={handleChange}
          className="w-full max-w-md"
          variant="secondary"
        />
      </div>

      {/* Passport Upload Section */}
      <div className="mb-6 flex flex-wrap gap-4 items-center">
        <label className="block font-normal text-gray-500">
          A copy of Passport and government-issued identification (identity
          proof)
        </label>
        <input
          ref={passportInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          onChange={(e) => setPassportFile(e.target.files?.[0] || null)}
        />
        <button
          type="button"
          title={passportFile ? "Replace document" : "Upload document"}
          onClick={() => passportInputRef.current?.click()}
          className="text-[#06513D] hover:text-[#387464] transition-colors cursor-pointer"
        >
          <Upload size={22} />
        </button>

        {passportFile ? (
          <span
            className="text-sm text-gray-600 truncate max-w-[160px]"
            title={passportFile.name}
          >
            {passportFile.name}
          </span>
        ) : contactDetails.copyOfPassport ? (
          <span
            className="text-sm text-emerald-700 font-medium truncate max-w-[160px]"
            title={contactDetails.copyOfPassport.split("/").pop()}
          >
            {contactDetails.copyOfPassport.split("/").pop() || "Uploaded"}
          </span>
        ) : null}

        {(passportFile || contactDetails.copyOfPassport) && (
          <button
            type="button"
            title="View document"
            onClick={() =>
              window.open(passportViewUrl, "_blank", "noopener,noreferrer")
            }
            className="text-[#06513D] hover:text-[#387464] transition-colors cursor-pointer"
          >
            <Eye size={22} />
          </button>
        )}

        {!passportFile && !contactDetails.copyOfPassport && (
          <span className="text-red-500 text-sm">Not Available</span>
        )}
      </div>

      {/* Contact Number & Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
        <div>
          <label className="block font-normal text-gray-500 mb-2">
            Contact Number
          </label>
          <PhoneNumberInput
            disable={false}
            value={contactDetails.contactNumber}
            onChange={(updatedValue) =>
              setContactDetails((prev) => ({
                ...prev,
                contactNumber: updatedValue,
              }))
            }
          />
        </div>

        <div>
          <label className="block font-normal text-gray-500 mb-2">
            Email ID
          </label>

          {/* Informational note about the currently saved / pending email —
              shown as text, never poured into the (intentionally blank) input. */}
          {!hasDraftEmail && savedBillingEmail && (
            <p className="text-xs text-gray-400 mb-2">
              Saved billing email: {savedBillingEmail}
              {isProfileEmailVerified ? " (verified)" : " (unverified)"}
            </p>
          )}
          {!hasDraftEmail && hasPendingVerificationOnProfile && (
            <p className="text-xs text-amber-600 mb-2">
              A verification is pending for {pendingBillingEmail}. Enter it
              below to resend the code or verify it.
            </p>
          )}

          <div className="flex gap-2 mb-4">
            <Input
              type="email"
              name="emailId"
              className="flex-1"
              value={contactDetails.emailId}
              onChange={handleChange}
              placeholder="Enter Email Address"
              variant="secondary"
            />
            <button
              type="button"
              onClick={!billingOtpLoading ? handleSendCode : undefined}
              className={`h-[40px] px-4 rounded-md border flex items-center justify-center transition-colors text-xs font-semibold whitespace-nowrap ${
                billingOtpSent
                  ? "bg-[#06513D] text-white border-[#06513D] hover:bg-[#387464]"
                  : "text-[#06513D] border-[#06513D] hover:bg-[#06513D] hover:text-white"
              } ${billingOtpLoading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {billingOtpLoading && !billingOtpSent
                ? "Sending..."
                : billingOtpSent
                  ? "Resend Code"
                  : "Send Code"}
            </button>
          </div>

          {showOtpInput && (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-28 px-2 py-2 border-b border-[#06513D] focus:outline-none bg-transparent"
              />
              <button
                type="button"
                onClick={!billingOtpLoading ? handleVerifyOtp : undefined}
                className={`h-[40px] px-4 rounded-md border flex items-center justify-center transition-colors text-xs font-semibold ${
                  billingOtpVerified
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-[#06513D] text-white border-[#06513D] hover:bg-[#387464]"
                } ${billingOtpLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {billingOtpLoading ? "Verifying..." : "Verify"}
              </button>
            </div>
          )}

          {emailChanged && !showEmailVerified && (
            <p className="text-red-500 text-xs mt-2">
              Verify this email before saving. Unverified changes will not be
              saved.
            </p>
          )}

          {showEmailVerified && (
            <div className="flex items-center gap-2 text-green-600 mt-2 text-sm font-medium">
              <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                &#10003;
              </div>
              Email Verified Successfully
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactDetails;
